import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { MagicWand, Eye, ArrowClockwise, Rows, Columns, Sparkle } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { PromoPageHeader } from "../components/PromoPageHeader";
import { EmptyState } from "../components/EmptyState";
import { PromoSelect } from "../components/PromoSelect";
import { SocialPreviewCard } from "../components/SocialPreviewCard";
import { PreviewDetailPanel } from "../components/preview/PreviewDetailPanel";
import { useWizardData, fetchProducts } from "../usePromo";
import { SOCIAL_PLATFORMS, MARKETPLACES } from "../platforms";

const MODES = ["original", "ai_enhance", "ai_complete"];
const LANGS = [["en", "English"], ["it", "Italiano"], ["es", "Español"], ["fr", "Français"], ["de", "Deutsch"], ["pt", "Português"]];

export default function PreviewCenter() {
  const { t } = useTranslation();
  const wd = useWizardData();
  const [connectionId, setConnectionId] = useState(null);
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState(null);
  const [mode, setMode] = useState("ai_complete");
  const [provider, setProvider] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [kb, setKb] = useState(null);
  const [language, setLanguage] = useState("en");
  const [platforms, setPlatforms] = useState(["facebook", "instagram", "linkedin"]);
  const [view, setView] = useState("grid");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  // Default source once sources load.
  useEffect(() => {
    if (!connectionId && wd.sources.length) {
      setConnectionId(wd.sources[0].id);
    }
  }, [wd.sources, connectionId]);

  const source = wd.sources.find((s) => s.id === connectionId);

  useEffect(() => {
    if (!source) return;
    let alive = true;
    fetchProducts(source.connector_id).then((list) => {
      if (!alive) return;
      setProducts(list || []);
      setProductId((list || [])[0]?.external_id || null);
    });
    return () => { alive = false; };
  }, [source]);

  const togglePlatform = (k) => setPlatforms((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);

  const run = async () => {
    if (!source || !productId || platforms.length === 0) return;
    setLoading(true);
    try {
      const ai = {};
      if (provider) ai.provider = provider;
      if (prompt) ai.prompt_template = prompt;
      if (kb) ai.kb_id = kb;
      const { data } = await api.post("/promotions/preview-product", {
        product_external_id: productId,
        source_platform: source.connector_id,
        connection_id: source.id,
        platforms,
        generation_mode: mode,
        language,
        ai,
      });
      setResult(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const productName = useMemo(() => products.find((p) => p.external_id === productId)?.title, [products, productId]);
  const opt = (arr) => arr;

  const previews = result?.previews || [];

  return (
    <div className="space-y-6" data-testid="preview-center">
      <PromoPageHeader overline={t("promo.nav.preview")} title={t("promo.preview.title")} description={t("promo.preview.subtitle")}
        actions={
          <button onClick={run} disabled={loading || !productId || platforms.length === 0}
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50" data-testid="preview-generate">
            <MagicWand size={16} weight="fill" /> {t("promo.preview.generate")}
          </button>
        } />

      {/* Context chips */}
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="preview-context">
        <span className="rounded-full border border-border bg-card px-2.5 py-1">{t("promo.preview.f.product")}: <span className="font-medium">{productName || "—"}</span></span>
        <span className="rounded-full border border-ai/30 bg-ai/10 px-2.5 py-1 text-ai">{t(`promo.wizard.mode.opt.${mode}.title`)}</span>
        <span className="rounded-full border border-border bg-card px-2.5 py-1">{provider || t("promo.wizard.optAuto")}</span>
        <span className="rounded-full border border-border bg-card px-2.5 py-1">{platforms.length} {t("promo.preview.platforms")}</span>
      </div>

      {/* Toolbar */}
      <div className="grid grid-cols-2 gap-3 rounded-lg border border-border bg-card p-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7" data-testid="preview-toolbar">
        <div className="space-y-1.5"><Label className="text-xs">{t("promo.preview.f.marketplace")}</Label>
          <PromoSelect value={connectionId} onChange={setConnectionId} testId="preview-marketplace"
            options={wd.sources.map((s) => ({ value: s.id, label: `${s.name} (${MARKETPLACES.find((m) => m.key === s.connector_id)?.label || s.connector_id})` }))} /></div>
        <div className="space-y-1.5"><Label className="text-xs">{t("promo.preview.f.product")}</Label>
          <PromoSelect value={productId} onChange={setProductId} testId="preview-product"
            options={products.map((p) => ({ value: p.external_id, label: p.title }))} /></div>
        <div className="space-y-1.5"><Label className="text-xs">{t("promo.wizard.steps.mode")}</Label>
          <PromoSelect value={mode} onChange={setMode} testId="preview-mode"
            options={MODES.map((m) => ({ value: m, label: t(`promo.wizard.mode.opt.${m}.title`) }))} /></div>
        <div className="space-y-1.5"><Label className="text-xs">{t("promo.wizard.steps.provider")}</Label>
          <PromoSelect value={provider} onChange={setProvider} testId="preview-provider" placeholder={t("promo.wizard.optAuto")}
            options={[{ value: null, label: t("promo.wizard.optAuto") }, ...wd.providers.map((p) => ({ value: p.provider, label: `${p.provider} · ${p.model}` }))]} /></div>
        <div className="space-y-1.5"><Label className="text-xs">{t("promo.wizard.steps.prompt")}</Label>
          <PromoSelect value={prompt} onChange={setPrompt} testId="preview-prompt" placeholder={t("promo.wizard.optAuto")}
            options={[{ value: null, label: t("promo.wizard.optAuto") }, ...wd.prompts.map((p) => ({ value: p.key, label: p.key }))]} /></div>
        <div className="space-y-1.5"><Label className="text-xs">{t("promo.wizard.steps.kb")}</Label>
          <PromoSelect value={kb} onChange={setKb} testId="preview-kb" placeholder={t("promo.wizard.optNone")}
            options={[{ value: null, label: t("promo.wizard.optNone") }, ...wd.kbs.map((k) => ({ value: k.id, label: k.name }))]} /></div>
        <div className="space-y-1.5"><Label className="text-xs">{t("promo.wizard.steps.language")}</Label>
          <PromoSelect value={language} onChange={setLanguage} testId="preview-language"
            options={LANGS.map(([v, l]) => ({ value: v, label: l }))} /></div>
      </div>

      {/* Platform selector */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2" data-testid="preview-platform-selector">
          {SOCIAL_PLATFORMS.map((p) => {
            const on = platforms.includes(p.key);
            return (
              <button key={p.key} onClick={() => togglePlatform(p.key)} data-testid={`preview-platform-${p.key}`}
                className={cn("flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors",
                  on ? "border-brand/30 bg-brand/10 text-brand" : "border-border bg-card text-muted-foreground hover:bg-accent/40")}>
                <p.Icon size={14} /> {p.label}
              </button>
            );
          })}
        </div>
        {previews.length > 0 && (
          <div className="flex overflow-hidden rounded-md border border-border">
            <button onClick={() => setView("grid")} className={cn("flex items-center gap-1.5 px-3 py-1.5 text-xs transition-colors", view === "grid" ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-accent/50")} data-testid="preview-view-grid"><Rows size={14} /> {t("promo.preview.grid")}</button>
            <button onClick={() => setView("compare")} className={cn("flex items-center gap-1.5 border-l border-border px-3 py-1.5 text-xs transition-colors", view === "compare" ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-accent/50")} data-testid="preview-view-compare"><Columns size={14} /> {t("promo.preview.compare")}</button>
          </div>
        )}
      </div>

      {/* Results */}
      {loading ? (
        <div className="promo-beam rounded-lg border border-border bg-card p-12 text-center text-sm text-muted-foreground" data-testid="preview-loading">{t("promo.wizard.preview.generating")}</div>
      ) : !result ? (
        <EmptyState Icon={Eye} title={t("promo.preview.emptyTitle")} description={t("promo.preview.emptyDesc")}
          action={<button onClick={run} disabled={!productId} className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50" data-testid="preview-empty-generate"><MagicWand size={15} weight="fill" /> {t("promo.preview.generate")}</button>} />
      ) : !result.product || previews.length === 0 ? (
        <EmptyState Icon={Eye} title={t("promo.wizard.preview.emptyTitle")} description={t("promo.wizard.preview.emptyDesc")} />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-3 text-sm">
            <Sparkle size={15} className="text-ai" />
            <span className="text-muted-foreground">{t("promo.wizard.preview.product")}: </span><span className="font-medium">{result.product.title}</span>
          </div>
          <div className={cn(view === "compare"
            ? "flex snap-x gap-4 overflow-x-auto pb-2"
            : "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3")} data-testid="preview-results">
            {previews.map((pv, i) => (
              <motion.button key={pv.platform} onClick={() => setSelected(pv)}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2, delay: i * 0.05 }}
                className={cn("text-left", view === "compare" && "w-80 shrink-0 snap-start")}
                data-testid={`preview-open-${pv.platform}`}>
                <SocialPreviewCard preview={pv} index={i} />
              </motion.button>
            ))}
          </div>
        </div>
      )}

      <PreviewDetailPanel preview={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
